export const BULK_SUBSCRIPTION_LP_BASE_PATH = '/lp';

export const toTitleCase = (value = '') => String(value)
  .split('-')
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

export const getLandingCodeFromPathname = (pathname = '') => {
  const match = String(pathname).match(/^\/lp\/([^/?#]+)/);

  if (!match?.[1]) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch (error) {
    return match[1];
  }
};

export const getLandingCodeFromSearch = (search = '') => new URLSearchParams(search).get('p');

export const getLandingCodeFromLocation = (location = {}) => (
  getLandingCodeFromPathname(location.pathname)
  || getLandingCodeFromSearch(location.search)
);

export const getLandingCode = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return getLandingCodeFromLocation(window.location);
};

export const getCleanLandingPath = (landingCode) => (
  landingCode
    ? `${BULK_SUBSCRIPTION_LP_BASE_PATH}/${landingCode}`
    : BULK_SUBSCRIPTION_LP_BASE_PATH
);

export const getSchoolNameFromLandingCode = (landingCode) => (
  landingCode ? toTitleCase(landingCode) : 'your school'
);

const parseMaybeJson = (value, fallback = {}) => {
  if (typeof value !== 'string') {
    return value || fallback;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

export const getSponsor = (promotion) => (
  promotion?.sponsor
  || promotion?.publisher
  || promotion
  || {}
);

export const getLandingData = (promotion) => {
  const sponsor = getSponsor(promotion);
  const landingData = promotion?.landing_data || sponsor?.landing_data;

  return parseMaybeJson(landingData, {});
};

export const getPublisherName = (publisher) => {
  const sponsor = getSponsor(publisher);
  const landingData = getLandingData(publisher);

  return (
    sponsor?.name
    || publisher?.name
    || sponsor?.title
    || publisher?.title
    || landingData?.publisher_name
    || landingData?.school_name
    || null
  );
};

export const getSponsorNickname = (publisher, fallbackName) => {
  const sponsor = getSponsor(publisher);
  const landingData = getLandingData(publisher);

  return (
    sponsor?.nickname
    || publisher?.nickname
    || landingData?.nickname
    || landingData?.sponsor_nickname
    || fallbackName
    || null
  );
};

export const getEmailDomain = (publisher) => {
  const sponsor = getSponsor(publisher);
  const landingData = getLandingData(publisher);

  return (
    landingData?.email_domain
    || sponsor?.email_domain
    || publisher?.email_domain
    || null
  );
};

export const getSponsorBranding = (publisher) => {
  const sponsor = getSponsor(publisher);
  const landingData = getLandingData(publisher);
  const branding = sponsor?.branding || publisher?.branding || landingData?.branding;

  return parseMaybeJson(branding, {});
};

// Brand colors are provided by the API, but a white or very pale color can
// disappear on this landing page's white background (and behind white CTA text).
// Keep accessible brand colors unchanged; darken light colors of the same hue.
const FALLBACK_BRAND_COLOR = '#06326e';

const parseBrandColor = (value) => {
  if (typeof value !== 'string') return null;

  const color = value.trim().toLowerCase();
  const namedColors = {
    white: '#ffffff',
    black: '#000000',
    navy: '#000080',
    red: '#ff0000',
    orange: '#ffa500',
    yellow: '#ffff00',
    blue: '#0000ff',
    green: '#008000',
    purple: '#800080',
    gray: '#808080',
    grey: '#808080',
    pink: '#ffc0cb',
    cyan: '#00ffff',
    teal: '#008080',
  };
  const normalized = namedColors[color] || color;
  const hex = normalized.match(/^#([a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/);

  if (hex) {
    const shorthand = hex[1].length <= 4;
    const chunks = shorthand
      ? hex[1].split('').map((char) => `${char}${char}`)
      : hex[1].match(/.{2}/g);
    const channels = chunks.map((channel) => parseInt(channel, 16));
    const alpha = channels.length === 4 ? channels[3] / 255 : 1;
    return channels.slice(0, 3)
      .map((channel) => Math.round((channel * alpha) + (255 * (1 - alpha))));
  }

  const rgb = normalized.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/,
  );
  if (!rgb) return null;

  const alpha = rgb[4] === undefined ? 1 : Number(rgb[4]);
  const channels = rgb.slice(1, 4).map(Number);
  if (channels.some((channel) => channel < 0 || channel > 255)
    || alpha < 0 || alpha > 1) return null;

  return channels.map((channel) => Math.round((channel * alpha) + (255 * (1 - alpha))));
};

const brandContrastOnWhite = (rgb) => {
  const luminance = rgb
    .map((channel) => {
      const value = channel / 255;
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    })
    .reduce((sum, value, index) => sum + value * [0.2126, 0.7152, 0.0722][index], 0);

  return 1.05 / (luminance + 0.05);
};

const rgbToHex = (rgb) => `#${rgb.map((value) => Math.round(value)
  .toString(16).padStart(2, '0')).join('')}`;

export const getSponsorBrandColor = (publisher, fallback = FALLBACK_BRAND_COLOR) => {
  const original = getSponsorBranding(publisher)?.color;
  const brandRgb = parseBrandColor(original);
  if (!brandRgb) return fallback;

  if (brandContrastOnWhite(brandRgb) >= 4.5) return original.trim();

  // A nearly white/gray logo color has no useful hue to preserve for an action.
  if (Math.max(...brandRgb) - Math.min(...brandRgb) < 20
    && Math.min(...brandRgb) > 215) return fallback;

  // Darken just enough for white button labels and text on light surfaces.
  const { low } = Array.from({ length: 14 }).reduce((range) => {
    const scale = (range.low + range.high) / 2;
    const darkened = brandRgb.map((channel) => Math.floor(channel * scale));
    return brandContrastOnWhite(darkened) >= 4.55
      ? { low: scale, high: range.high }
      : { low: range.low, high: scale };
  }, { low: 0, high: 1 });

  return rgbToHex(brandRgb.map((channel) => Math.floor(channel * low)));
};

export const getSponsorBrandTint = (publisher, fallback = '#edf5ff') => (
  getSponsorBranding(publisher)?.tint || fallback
);

export const getLandingImageCollectionUrls = (publisher) => {
  const imageCollectionUrls = getLandingData(publisher)?.image_collection_urls;
  const parsedUrls = parseMaybeJson(imageCollectionUrls, imageCollectionUrls);

  if (Array.isArray(parsedUrls)) {
    return parsedUrls.filter(Boolean);
  }

  if (typeof parsedUrls === 'string' && parsedUrls.trim()) {
    return [parsedUrls.trim()];
  }

  return [];
};

export const interpolateLandingText = (text = '', values = {}) => {
  const replacements = {
    SCHOOL_NAME: values.schoolName,
    school_name: values.schoolName,
    SCHOOL_NICKNAME: values.schoolNickname,
    school_nickname: values.schoolNickname,
    NICKNAME: values.schoolNickname,
    nickname: values.schoolNickname,
    EMAIL_DOMAIN: values.emailDomain,
    email_domain: values.emailDomain,
  };

  return Object.entries(replacements).reduce((result, [key, value]) => {
    if (!value) return result;

    return result
      .replace(new RegExp(`%${key}%`, 'g'), () => value)
      .replace(new RegExp(`\\{${key}\\}`, 'g'), () => value)
      .replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), () => value);
  }, String(text));
};

const isGenericSchoolName = (schoolName) => (
  !schoolName || String(schoolName).toLowerCase() === 'your school'
);

export const buildBulkSubscriptionSeo = (schoolName, landingCode) => {
  const safeSchoolName = schoolName || null;
  const isGeneric = isGenericSchoolName(safeSchoolName);
  const schoolSegment = isGeneric ? 'Students' : `${safeSchoolName} Students`;
  const canonicalPath = getCleanLandingPath(landingCode);

  return {
    title: isGeneric
      ? 'Free Premium Student News Access | SpotlightNews'
      : `Free News for ${safeSchoolName} Students | SpotlightNews`,
    description: isGeneric
      ? 'Students get free access to 400+ trusted news publishers with their .edu email. No paywalls. Activate your SpotlightNews account today.'
      : `${safeSchoolName} students get free access to 400+ trusted news publishers with their .edu email. No paywalls. Activate your SpotlightNews account today.`,
    canonicalPath,
    keywords: isGeneric
      ? 'free student news, campus news, college news, trusted news publishers, SpotlightNews'
      : `${safeSchoolName} news, ${safeSchoolName} student news, free student news, campus news, college news, SpotlightNews`,
    ogTitle: `Free Premium News for ${schoolSegment}`,
    ogDescription: isGeneric
      ? 'Students get free access to hundreds of trusted news publishers through SpotlightNews. Verify your .edu email to get started.'
      : `${safeSchoolName} provides students with free access to hundreds of trusted news publishers through SpotlightNews. Verify your .edu email to get started.`,
  };
};

// Publisher fallback must not claim an uncatalogued school promotion or benefit.
export const buildPublisherLandingSeo = (publisherName, landingCode, description) => ({
  title: publisherName ? `Discover ${publisherName} on SpotlightNews` : 'Discover News Publishers | SpotlightNews',
  description: description || (publisherName
    ? `Discover stories from ${publisherName} and follow trusted news sources on SpotlightNews.`
    : 'Find and follow trusted news publishers in SpotlightNews.'),
  canonicalPath: getCleanLandingPath(landingCode),
  keywords: publisherName ? `${publisherName}, news, publishers, SpotlightNews` : 'news, publishers, SpotlightNews',
  ogTitle: publisherName ? `Discover ${publisherName} on SpotlightNews` : 'Discover News Publishers | SpotlightNews',
  ogDescription: description || (publisherName
    ? `Read and follow ${publisherName} on SpotlightNews.`
    : 'Discover trusted news publishers on SpotlightNews.'),
});
